// src/components/Manager/MeetingRoomManager.jsx
"use client";

import { useEffect, useState, useRef } from "react"
import {
  Mic,
  MicOff,
  Clock,
  Users,
  FileText,
  CheckSquare,
  Download,
  Edit3,
  CheckCircle,
  AudioLines,
  Globe,
  Calendar,
  MapPin,
  Target,
  X,
  ChevronDown,
  Wifi,
  WifiOff,
  User,
  Flag,
  RotateCcw,
  Loader2,
  UserPlus,
  Mail,
  Sparkles,
  Bell,
  Check,
  AlertCircle,
} from "lucide-react"
import { useParams, useNavigate } from 'react-router-dom'
import FinalizeMeetingPopup from './FinalizeMeetingPopup'
import QuitMeetingPopup from './QuitMeetingPopup'

const rawBackendUrl = process.env.REACT_APP_BACKEND_URL || "";
const backend_url = rawBackendUrl.startsWith("http://") || rawBackendUrl.startsWith("https://") ? rawBackendUrl : `https://${rawBackendUrl}`;
const API_URL = `${backend_url}/api`
const AI_SERVICE_URL = (process.env.REACT_APP_AI_SERVICE_URL || "https://your-ai-service.railway.app").replace(/\/+$/, "")
const getToken = () => localStorage.getItem('access_token')

const LANGUAGES = [
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
]

const generateEmailFromName = (name) => {
  if (!name) return ""
  const cleanName = name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '.')
  return `${cleanName}@polymeet.com`
}

export default function MeetingRoomManager() {
  const navigate = useNavigate()
  const params = useParams()
  const meetingId = params.id

  const [seconds, setSeconds] = useState(0)
  const [recording, setRecording] = useState(false)
  const [meetingDetails, setMeetingDetails] = useState(null)
  const [participants, setParticipants] = useState([])
  const [loading, setLoading] = useState(true)
  const [showParticipants, setShowParticipants] = useState(false)
  const [showQuitPopup, setShowQuitPopup] = useState(false)

  const [transcription, setTranscription] = useState("")
  const [interimText, setInterimText] = useState("")
  const [translation, setTranslation] = useState("")
  const [compteRendu, setCompteRendu] = useState("")
  const [tasks, setTasks] = useState([])
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false)
  const [isExtractingTasks, setIsExtractingTasks] = useState(false)
  const [notification, setNotification] = useState({ show: false, message: "", type: "" })

  const [translationLang, setTranslationLang] = useState('en')
  const [showTranslationLang, setShowTranslationLang] = useState(false)
  const [detectedLang, setDetectedLang] = useState('fr')
  const [activeTab, setActiveTab] = useState('transcription')

  const [editCR, setEditCR] = useState(false)
  const [editTasks, setEditTasks] = useState(false)
  const [error, setError] = useState("")

  const [tempCompteRendu, setTempCompteRendu] = useState("")
  const [tempTasks, setTempTasks] = useState([])

  const [showFinalizePopup, setShowFinalizePopup] = useState(false)
  const [onlineParticipants, setOnlineParticipants] = useState([])
  const [currentUser, setCurrentUser] = useState(null)

  // Audio test states
  const [isPlayingAudio, setIsPlayingAudio] = useState(false)
  const [audioProgress, setAudioProgress] = useState(0)
  const audioRef = useRef(null)

  // Refs pour la reconnaissance vocale
  const recognitionRef = useRef(null)
  const finalTranscriptRef = useRef("")
  const translationTimeoutRef = useRef(null)
  const autoAnalyzeIntervalRef = useRef(null)
  const autoRestartIntervalRef = useRef(null)

  const showNotification = (message, type = "success") => {
    setNotification({ show: true, message, type })
    setTimeout(() => {
      setNotification({ show: false, message: "", type: "" })
    }, 3000)
  }

  useEffect(() => {
    const authData = JSON.parse(localStorage.getItem("auth_data") || "{}")
    setCurrentUser({
      id: authData.userId,
      name: authData.name,
      email: authData.email
    })
  }, [])

  // Audio progress tracking
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return
    
    const updateProgress = () => {
      if (audio.duration) {
        setAudioProgress((audio.currentTime / audio.duration) * 100)
      }
    }
    
    const handleEnded = () => {
      setIsPlayingAudio(false)
      setAudioProgress(0)
    }
    
    audio.addEventListener('timeupdate', updateProgress)
    audio.addEventListener('ended', handleEnded)
    
    return () => {
      audio.removeEventListener('timeupdate', updateProgress)
      audio.removeEventListener('ended', handleEnded)
    }
  }, [])

  useEffect(() => {
    if (participants.length === 0) return
    const initialOnline = participants.filter(p => p.id === currentUser?.id).map(p => p.id)
    setOnlineParticipants(initialOnline)

    const interval = setInterval(() => {
      if (participants.length > 0 && recording) {
        setOnlineParticipants(prev => {
          const newOnline = [...prev]
          const offlineParticipants = participants.filter(p => !prev.includes(p.id) && p.id !== currentUser?.id)
          if (offlineParticipants.length > 0 && Math.random() > 0.7) {
            const randomIndex = Math.floor(Math.random() * offlineParticipants.length)
            newOnline.push(offlineParticipants[randomIndex].id)
          }
          const onlineNotCurrent = prev.filter(id => id !== currentUser?.id)
          if (onlineNotCurrent.length > 0 && Math.random() > 0.8) {
            const randomIndex = Math.floor(Math.random() * onlineNotCurrent.length)
            const indexToRemove = newOnline.indexOf(onlineNotCurrent[randomIndex])
            if (indexToRemove > -1) newOnline.splice(indexToRemove, 1)
          }
          return newOnline
        })
      }
    }, 10000)
    return () => clearInterval(interval)
  }, [participants, recording, currentUser])

  const detectLanguage = (text) => {
    if (!text) return 'fr'
    if (/[\u0600-\u06FF]/.test(text)) return 'ar'
    if (/[àâäéèêëïîôöùûüÿçœ]/.test(text)) return 'fr'
    if (/[ñáéíóúü¿¡]/.test(text)) return 'es'
    if (/[äöüß]/.test(text)) return 'de'
    if (/[àèéìíòóù]/.test(text)) return 'it'
    return 'en'
  }

  const translateText = async (text, targetLang) => {
    if (!text.trim()) return

    if (translationTimeoutRef.current) {
      clearTimeout(translationTimeoutRef.current)
    }

    translationTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await fetch(`${AI_SERVICE_URL}/translate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: text.trim(), target_lang: targetLang })
        })

        if (response.ok) {
          const data = await response.json()
          setTranslation(prev => prev + data.translated_text + '\n')
        }
      } catch (error) {
        console.error("Erreur traduction:", error)
      }
    }, 1000)
  }

  const generateSummary = async () => {
    if (!transcription || transcription.length < 100) {
      showNotification("Pas assez de texte pour générer un résumé (minimum 100 caractères)", "error")
      return
    }

    setIsGeneratingSummary(true)
    try {
      const response = await fetch(`${AI_SERVICE_URL}/summarize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: transcription,
          title: meetingDetails?.titre || "",
          objective: meetingDetails?.objectif || ""
        })
      })

      if (response.ok) {
        const data = await response.json()
        setCompteRendu(data.summary)
        showNotification("Résumé généré avec succès !", "success")
      } else {
        showNotification("Erreur lors de la génération du résumé", "error")
      }
    } catch (error) {
      console.error("Erreur génération résumé:", error)
      showNotification("Erreur de connexion au service IA", "error")
    } finally {
      setIsGeneratingSummary(false)
    }
  }

  const generateTasks = async () => {
    if (!transcription || transcription.length < 100) {
      showNotification("Pas assez de texte pour extraire les tâches", "error")
      return
    }

    setIsExtractingTasks(true)
    try {
      const response = await fetch(`${AI_SERVICE_URL}/extract-tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: transcription })
      })

      if (response.ok) {
        const data = await response.json()
        if (data.tasks && data.tasks.length > 0) {
          const tasksWithAssignees = data.tasks.map((task, idx) => {
            let responsableName = ""
            let deadline = ""
            const description = task.description.toLowerCase()
            
            for (const participant of participants) {
              const participantName = participant.name?.toLowerCase()
              if (participantName && description.includes(participantName)) {
                responsableName = participant.name
                break
              }
            }
            
            if (!responsableName && task.responsable) {
              responsableName = task.responsable
            }

            const datePatterns = [
              /(\d{1,2})\s*(janvier|février|mars|avril|mai|juin|juillet|août|septembre|octobre|novembre|décembre)/i,
              /(\d{1,2})\/(\d{1,2})\/(\d{4})/,
              /avant le (\d{1,2})\/(\d{1,2})/,
              /pour le (\d{1,2})\s*(janvier|février|mars|avril|mai|juin|juillet|août|septembre|octobre|novembre|décembre)/i,
              /(\d{1,2})\s*(du mois|mois prochain)/i
            ]

            for (const pattern of datePatterns) {
              const match = task.description.match(pattern)
              if (match) {
                if (match[0].includes("mois")) {
                  const currentDate = new Date()
                  const day = parseInt(match[1])
                  let targetDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day)
                  if (targetDate < currentDate) {
                    targetDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, day)
                  }
                  deadline = targetDate.toISOString().split('T')[0]
                } else {
                  deadline = match[0]
                }
                break
              }
            }

            if (task.deadline && !deadline) {
              deadline = task.deadline
            }

            let responsableId = ""
            let responsableEmail = ""
            const participant = participants.find(p =>
              p.name?.toLowerCase() === responsableName.toLowerCase()
            )
            if (participant) {
              responsableId = participant.id
              responsableEmail = participant.email
            }

            return {
              id: Date.now() + idx,
              description: task.description,
              responsableId: responsableId,
              responsableName: responsableName,
              responsableEmail: responsableEmail,
              priorite: task.priorite || "moyenne",
              deadline: deadline,
              statut: "a_faire"
            }
          })
          setTasks(tasksWithAssignees)
          showNotification(`${tasksWithAssignees.length} tâches extraites avec succès !`, "success")
        } else {
          setTasks([])
          showNotification("Aucune tâche détectée dans le texte", "info")
        }
      } else {
        showNotification("Erreur lors de l'extraction des tâches", "error")
      }
    } catch (error) {
      console.error("Erreur extraction tâches:", error)
      showNotification("Erreur de connexion au service IA", "error")
    } finally {
      setIsExtractingTasks(false)
    }
  }

  const addTask = () => {
    const newTask = {
      id: Date.now(),
      description: "",
      responsableId: "",
      responsableName: "",
      responsableEmail: "",
      priorite: "moyenne",
      deadline: "",
      statut: "a_faire"
    }
    setTasks([...tasks, newTask])
    setEditTasks(true)
  }

  useEffect(() => {
    if (meetingId) fetchMeetingData()
  }, [meetingId])

  const fetchMeetingData = async () => {
    try {
      const token = getToken()
      if (!token) {
        setError("Vous devez être connecté")
        setLoading(false)
        return
      }
      const res = await fetch(`${API_URL}/reunions/${meetingId}/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (!res.ok) throw new Error('Erreur chargement')
      const data = await res.json()
      setMeetingDetails(data)
      setParticipants(data.participants_details || [])
    } catch (error) {
      setError("Impossible de charger la réunion")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!recording) return
    const interval = setInterval(() => setSeconds(s => s + 1), 1000)
    return () => clearInterval(interval)
  }, [recording])

  useEffect(() => {
    if (recording && transcription.length > 150) {
      if (autoAnalyzeIntervalRef.current) clearInterval(autoAnalyzeIntervalRef.current)

      autoAnalyzeIntervalRef.current = setInterval(() => {
        if (transcription.length > 150) {
          generateSummary()
          generateTasks()
        }
      }, 30000)

      return () => {
        if (autoAnalyzeIntervalRef.current) clearInterval(autoAnalyzeIntervalRef.current)
      }
    }
  }, [recording, transcription])

  const playTestAudio = () => {
    if (audioRef.current) {
      if (isPlayingAudio) {
        audioRef.current.pause()
        setIsPlayingAudio(false)
      } else {
        audioRef.current.play()
        setIsPlayingAudio(true)
      }
    }
  }

  const initSpeechRecognition = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition

    if (!SpeechRecognition) {
      setError("Votre navigateur ne supporte pas la reconnaissance vocale. Utilisez Chrome.")
      return null
    }

    const recognition = new SpeechRecognition()
    recognition.continuous = true
    recognition.interimResults = true
    recognition.lang = 'fr-FR'
    recognition.maxAlternatives = 1
    
    let restartAttempts = 0
    let lastRestartTime = Date.now()

    recognition.onstart = () => {
      console.log("🎤 Reconnaissance démarrée")
      setError("")
      restartAttempts = 0
      showNotification("Reconnaissance vocale activée - Parlez distinctement", "success")
    }

    recognition.onerror = (event) => {
      console.error("Erreur:", event.error)
      
      if (event.error === 'network' || event.error === 'audio-capture' || event.error === 'no-speech') {
        console.log("🔄 Tentative de redémarrage...")
        
        const now = Date.now()
        if (now - lastRestartTime > 1000) {
          lastRestartTime = now
          restartAttempts++
          
          if (restartAttempts <= 5) {
            setTimeout(() => {
              if (recording && recognitionRef.current) {
                try {
                  recognitionRef.current.start()
                  console.log(`✅ Redémarrage ${restartAttempts}/5 réussi`)
                } catch (e) {
                  console.error("❌ Erreur redémarrage:", e)
                }
              }
            }, 500)
          } else {
            setError("Problème de microphone. Vérifiez vos permissions.")
            showNotification("Problème de microphone", "error")
          }
        }
      } else if (event.error === 'not-allowed') {
        setError("Veuillez autoriser l'accès au microphone")
        showNotification("Microphone non autorisé", "error")
      }
    }

    recognition.onend = () => {
      console.log("Reconnaissance terminée")
      
      if (recording) {
        console.log("🔄 Redémarrage automatique de la reconnaissance...")
        setTimeout(() => {
          if (recording && recognitionRef.current) {
            try {
              recognitionRef.current.start()
              console.log("✅ Reconnaissance redémarrée avec succès")
            } catch (e) {
              console.error("❌ Échec redémarrage:", e)
              setTimeout(() => {
                if (recording && recognitionRef.current) {
                  try {
                    recognitionRef.current = initSpeechRecognition()
                    if (recognitionRef.current) {
                      recognitionRef.current.start()
                    }
                  } catch (err) {
                    console.error("❌ Échec redémarrage final:", err)
                  }
                }
              }, 1000)
            }
          }
        }, 100)
      }
    }

    recognition.onresult = (event) => {
      let final = ''
      let interim = ''

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript
        const confidence = event.results[i][0].confidence
        
        if (event.results[i].isFinal) {
          final += transcript + ' '
          finalTranscriptRef.current += transcript + ' '
          console.log(`✅ Final (${Math.round(confidence * 100)}%): ${transcript}`)
        } else {
          interim += transcript + ' '
        }
      }

      if (final) {
        let cleanedFinal = final
          .replace(/\s+/g, ' ')
          .trim()
        
        setTranscription(finalTranscriptRef.current)
        const lang = detectLanguage(cleanedFinal)
        if (lang !== detectedLang) setDetectedLang(lang)
        translateText(cleanedFinal, translationLang)
      }

      setInterimText(interim)
      restartAttempts = 0
    }

    return recognition
  }

  const startRecording = async () => {
    try {
      setError("")
      setTranscription("")
      setTranslation("")
      setCompteRendu("")
      setTasks([])
      finalTranscriptRef.current = ""

      const token = getToken()
      
      if (!token) {
        setError("Vous n'êtes pas connecté")
        showNotification("Veuillez vous reconnecter", "error")
        return
      }

      const response = await fetch(`${API_URL}/reunions/${meetingId}/demarrer/`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Erreur démarrage')
      }

      if (autoRestartIntervalRef.current) {
        clearInterval(autoRestartIntervalRef.current)
      }
      
      const recognition = initSpeechRecognition()
      if (recognition) {
        recognitionRef.current = recognition
        recognition.start()
        setRecording(true)
        setSeconds(0)
        
        autoRestartIntervalRef.current = setInterval(() => {
          if (recording && recognitionRef.current) {
            console.log("🔄 Restart périodique de la reconnaissance...")
            try {
              recognitionRef.current.stop()
              setTimeout(() => {
                if (recording && recognitionRef.current) {
                  try {
                    recognitionRef.current.start()
                  } catch (e) {
                    console.error("Erreur restart périodique:", e)
                  }
                }
              }, 100)
            } catch (e) {
              console.error("Erreur arrêt recognition:", e)
            }
          }
        }, 45000)
      }
    } catch (error) {
      console.error("Erreur démarrage:", error)
      setError("Impossible de démarrer: " + error.message)
      showNotification("Erreur: " + error.message, "error")
    }
  }

  const stopRecording = () => {
    setRecording(false)
    
    if (autoRestartIntervalRef.current) {
      clearInterval(autoRestartIntervalRef.current)
      autoRestartIntervalRef.current = null
    }
    
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop()
      } catch (e) { }
      recognitionRef.current = null
    }
    
    if (translationTimeoutRef.current) {
      clearTimeout(translationTimeoutRef.current)
    }
    
    if (autoAnalyzeIntervalRef.current) {
      clearInterval(autoAnalyzeIntervalRef.current)
    }
    
    showNotification("Enregistrement arrêté", "info")
  }

  const startEditCR = () => {
    setTempCompteRendu(compteRendu)
    setEditCR(true)
  }

  const saveEditCR = () => {
    setCompteRendu(tempCompteRendu)
    setEditCR(false)
    setTempCompteRendu("")
    showNotification("Compte rendu modifié", "success")
  }

  const cancelEditCR = () => {
    setEditCR(false)
    setTempCompteRendu("")
  }

  const startEditTasks = () => {
    setTempTasks([...tasks])
    setEditTasks(true)
  }

  const saveEditTasks = () => {
    setTasks(tempTasks)
    setEditTasks(false)
    setTempTasks([])
    showNotification("Tâches mises à jour", "success")
  }

  const cancelEditTasks = () => {
    setEditTasks(false)
    setTempTasks([])
  }

  const handleEndMeeting = async () => {
    if (recording) stopRecording()
    setShowFinalizePopup(true)
  }

  const handleFinalizeSave = async (data) => {
    console.log("✅ Réunion finalisée:", data)
    showNotification("Réunion finalisée avec succès !", "success")

    try {
      const token = getToken()
      await fetch(`${API_URL}/reunions/${meetingId}/terminer/`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      })

      setTimeout(() => {
        navigate("/listeReunions")
      }, 2000)
    } catch (error) {
      console.error("Erreur finalisation:", error)
      setError("Erreur lors de la finalisation")
    }
  }

  const handleQuit = () => {
    setShowQuitPopup(true)
  }

  const handleConfirmQuit = () => {
    stopRecording()
    setShowQuitPopup(false)
    navigate("/listeReunions")
  }

  const download = (text, name) => {
    const blob = new Blob([text], { type: "text/plain" })
    const a = document.createElement("a")
    a.href = URL.createObjectURL(blob)
    a.download = name
    a.click()
    showNotification(`Téléchargement de ${name}`, "success")
  }

  const getPriorityColor = (priority) => {
    const colors = {
      critique: '#ef4444',
      haute: '#f97316',
      moyenne: '#eab308',
      basse: '#16a34a'
    }
    return colors[priority] || colors.moyenne
  }

  const getPriorityBg = (priority) => {
    const bg = {
      critique: '#fee2e2',
      haute: '#ffedd5',
      moyenne: '#fef3c7',
      basse: '#dcfce7'
    }
    return bg[priority] || bg.moyenne
  }

  const getParticipantName = (participantId) => {
    const participant = participants.find(p => p.id === parseInt(participantId))
    return participant ? participant.name : ""
  }

  const getParticipantEmail = (participantId) => {
    const participant = participants.find(p => p.id === parseInt(participantId))
    return participant ? participant.email : ""
  }

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop()
        } catch (e) { }
      }
      if (translationTimeoutRef.current) {
        clearTimeout(translationTimeoutRef.current)
      }
      if (autoAnalyzeIntervalRef.current) {
        clearInterval(autoAnalyzeIntervalRef.current)
      }
      if (autoRestartIntervalRef.current) {
        clearInterval(autoRestartIntervalRef.current)
      }
    }
  }, [])

  if (loading) return (
    <div style={styles.loadingContainer}>
      <div style={styles.loadingSpinner}></div>
      <div style={styles.loadingText}>Chargement de la réunion...</div>
    </div>
  )

  const currentLang = LANGUAGES.find(l => l.code === detectedLang) || LANGUAGES[0]
  const targetLang = LANGUAGES.find(l => l.code === translationLang) || LANGUAGES[1]

  return (
    <div style={styles.container}>
      {notification.show && (
        <div style={{
          ...styles.notification,
          backgroundColor: notification.type === 'success' ? '#22c55e' : notification.type === 'error' ? '#ef4444' : '#3b82f6',
          animation: 'slideIn 0.3s ease-out'
        }}>
          {notification.type === 'success' && <Check size={18} />}
          {notification.type === 'error' && <AlertCircle size={18} />}
          {notification.type === 'info' && <Bell size={18} />}
          <span>{notification.message}</span>
        </div>
      )}

      <div style={styles.header}>
        <div style={styles.headerTop}>
          <div style={styles.titleSection}>
            <h1 style={styles.title}>{meetingDetails?.titre}</h1>
            {recording && (
              <div style={styles.recordingBadge}>
                <div style={styles.recordingDot}></div>
                <span>Enregistrement en cours</span>
              </div>
            )}
          </div>
          <div style={styles.headerButtons}>
            {!recording ? (
              <button style={styles.headerBtnGreen} onClick={startRecording}>
                <Mic size={16} /> Démarrer
              </button>
            ) : (
              <button style={styles.headerBtnRed} onClick={stopRecording}>
                <MicOff size={16} /> Stop
              </button>
            )}
            <button style={styles.headerBtnDark} onClick={handleEndMeeting}>
              Terminer
            </button>
            <button style={styles.headerBtnQuit} onClick={handleQuit}>
              <X size={16} /> Quitter
            </button>
          </div>
        </div>

        <div style={styles.meetingInfo}>
          <span style={styles.infoItem}>
            <Target size={14} color="#5e17eb" /> {meetingDetails?.objectif || "Objectif non spécifié"}
          </span>
          <span style={styles.infoItem}>
            <Calendar size={14} color="#5e17eb" /> {meetingDetails?.date_heure ? new Date(meetingDetails.date_heure).toLocaleString('fr-FR', {
              day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
            }) : 'Date non spécifiée'}
          </span>
          <span style={styles.infoItem}>
            <Clock size={14} color="#5e17eb" /> {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, "0")}
          </span>
          <span style={styles.infoItem}>
            <MapPin size={14} color="#5e17eb" /> {meetingDetails?.salle || "Salle non spécifiée"}
          </span>

          <div style={styles.participantsHeaderCompact}>
            <button style={styles.participantsButton} onClick={() => setShowParticipants(!showParticipants)}>
              <Users size={16} color="#5e17eb" />
              <span style={styles.participantsCountCompact}>{participants.length}</span>
              <ChevronDown size={14} style={{ transform: showParticipants ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }} />
            </button>

            {showParticipants && (
              <div style={styles.participantsDropdown}>
                <div style={styles.participantsDropdownHeader}>
                  <Users size={16} color="#5e17eb" />
                  <span>Participants ({participants.length})</span>
                  <span style={{ fontSize: 11, color: "#666", marginLeft: "auto" }}>
                    <Wifi size={12} /> Connectés: {onlineParticipants.length}
                  </span>
                </div>
                <div style={styles.participantsDropdownList}>
                  {participants.map((p, i) => {
                    const isOnline = onlineParticipants.includes(p.id)
                    const isCurrentUser = p.id === currentUser?.id
                    return (
                      <div key={i} style={styles.participantDropdownItem}>
                        <div style={styles.participantDropdownAvatar}>
                          {p.name?.charAt(0).toUpperCase()}
                        </div>
                        <div style={styles.participantDropdownInfo}>
                          <span style={styles.participantDropdownName}>
                            {p.name}
                            {isCurrentUser && <span style={{ fontSize: 10, color: "#5e17eb", marginLeft: 5 }}>(Vous)</span>}
                          </span>
                          <span style={styles.participantDropdownEmail}>{p.email}</span>
                        </div>
                        <div style={styles.participantDropdownStatus}>
                          {isOnline ? (
                            <span style={{ display: "flex", alignItems: "center", gap: 4, color: "#22c55e" }}>
                              <Wifi size={12} />
                              <span style={{ fontSize: 10 }}>En ligne</span>
                            </span>
                          ) : (
                            <span style={{ display: "flex", alignItems: "center", gap: 4, color: "#9ca3af" }}>
                              <WifiOff size={12} />
                              <span style={{ fontSize: 10 }}>Hors ligne</span>
                            </span>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* TEST AUDIO SECTION - FOR COMMISSION TESTING */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: 16,
        padding: '16px 24px',
        marginBottom: 20,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 16,
        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
        animation: 'slideIn 0.5s ease-out'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            background: 'white',
            borderRadius: '50%',
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 20
          }}>
            🎵
          </div>
          <div>
            <div style={{ color: 'white', fontWeight: 600, fontSize: 14 }}>
              🧪 Mode Test - Audio Intégré
            </div>
            <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12 }}>
              Cliquez sur "Jouer" pour tester la transcription avec cet audio de réunion test
            </div>
          </div>
        </div>
        
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ 
            background: 'rgba(255,255,255,0.2)', 
            borderRadius: 20,
            height: 6,
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${audioProgress}%`,
              height: '100%',
              background: 'white',
              transition: 'width 0.1s linear'
            }} />
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={playTestAudio}
            style={{
              background: 'white',
              border: 'none',
              borderRadius: 40,
              padding: '8px 24px',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontWeight: 600,
              fontSize: 14,
              cursor: 'pointer',
              color: '#667eea',
              transition: 'transform 0.2s'
            }}
            onMouseEnter={e => e.target.style.transform = 'scale(1.05)'}
            onMouseLeave={e => e.target.style.transform = 'scale(1)'}
          >
            {isPlayingAudio ? '⏸️ Pause' : '▶️ Jouer le test audio'}
          </button>
          
          <audio
            ref={audioRef}
            src="/audio/test-meeting.mp3"
            preload="auto"
          />
        </div>
        
        <div style={{ 
          background: 'rgba(255,255,255,0.15)', 
          padding: '4px 12px', 
          borderRadius: 20,
          fontSize: 11,
          color: 'white'
        }}>
          💡 Conseil: Démarrez l'enregistrement puis jouez l'audio
        </div>
      </div>

      {error && <div style={styles.errorToast}>{error}</div>}

      <div style={styles.mainArea}>
        <div style={styles.leftPanel}>
          <div style={styles.panelHeader}>
            <div style={styles.panelTitle}>
              <AudioLines size={20} color="#5e17eb" />
              <span>TRANSCRIPTION EN TEMPS RÉEL</span>
            </div>
            <div style={styles.langBadge}>
              <span>{currentLang.flag}</span>
              <span>{currentLang.name}</span>
            </div>
          </div>

          <div style={styles.transcriptionArea}>
            {transcription || interimText ? (
              <>
                <div style={styles.transcriptText}>{transcription}</div>
                {interimText && <div style={styles.interimText}>{interimText}</div>}
              </>
            ) : (
              <div style={styles.placeholderText}>
                {recording ? "Parlez... la transcription apparaît en temps réel" : "Cliquez sur Démarrer pour commencer"}
              </div>
            )}
          </div>
        </div>

        <div style={styles.rightPanel}>
          <div style={styles.tabBar}>
            <button
              style={activeTab === 'transcription' ? styles.tabActive : styles.tab}
              onClick={() => setActiveTab('transcription')}
            >
              <Globe size={16} />
              <span>Traduction</span>
              <span style={styles.tabLang}>{targetLang.flag}</span>
            </button>
            <button
              style={activeTab === 'cr' ? styles.tabActive : styles.tab}
              onClick={() => setActiveTab('cr')}
            >
              <FileText size={16} />
              <span>Compte rendu</span>
              {!compteRendu && !isGeneratingSummary && transcription.length > 100 && (
                <span style={styles.badgeNew}>Nouveau</span>
              )}
            </button>
            <button
              style={activeTab === 'tasks' ? styles.tabActive : styles.tab}
              onClick={() => setActiveTab('tasks')}
            >
              <CheckSquare size={16} />
              <span>Tâches</span>
              {tasks.length > 0 && <span style={styles.badgeCount}>{tasks.length}</span>}
            </button>
          </div>

          <div style={styles.tabContent}>
            {activeTab === 'transcription' && (
              <>
                <div style={styles.tabHeader}>
                  <div style={styles.translationControls}>
                    <span style={styles.translationLabel}>Traduire en</span>
                    <div style={styles.langSelector}>
                      <button
                        style={styles.langSelectorButton}
                        onClick={() => setShowTranslationLang(!showTranslationLang)}
                      >
                        <span>{targetLang.flag}</span>
                        <span>{targetLang.name}</span>
                      </button>
                      {showTranslationLang && (
                        <div style={styles.langDropdown}>
                          {LANGUAGES.map(lang => (
                            <div
                              key={lang.code}
                              style={styles.langOption}
                              onClick={() => {
                                setTranslationLang(lang.code)
                                setShowTranslationLang(false)
                                setTranslation("")
                              }}
                            >
                              <span>{lang.flag}</span>
                              <span>{lang.name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <textarea
                  style={styles.contentTextarea}
                  value={translation}
                  readOnly
                  placeholder="La traduction apparaîtra ici en temps réel..."
                />
              </>
            )}

            {activeTab === 'cr' && (
              <>
                <div style={styles.tabHeader}>
                  <span style={styles.tabHeaderTitle}>
                    <Sparkles size={14} color="#5e17eb" />
                    Compte rendu automatique
                  </span>
                  <button
                    style={styles.generateButton}
                    onClick={generateSummary}
                    disabled={isGeneratingSummary}
                  >
                    {isGeneratingSummary ? <Loader2 size={14} className="spin" /> : <RotateCcw size={14} />}
                    <span>{isGeneratingSummary ? "Génération..." : "Générer"}</span>
                  </button>
                </div>
                <div style={styles.crCard}>
                  <div style={styles.crHeader}>
                    <span style={styles.crTitle}>📄 {meetingDetails?.titre}</span>
                    <span style={styles.crDate}>{new Date().toLocaleDateString('fr-FR')}</span>
                  </div>
                  {editCR ? (
                    <textarea
                      style={styles.crEditTextarea}
                      value={tempCompteRendu}
                      onChange={(e) => setTempCompteRendu(e.target.value)}
                    />
                  ) : (
                    <div style={styles.crContent}>
                      {compteRendu ? (
                        <div style={styles.crText}>
                          {compteRendu.split('\n').map((line, i) => (
                            <p key={i} style={styles.crParagraph}>{line || <br />}</p>
                          ))}
                        </div>
                      ) : (
                        <div style={styles.crEmpty}>
                          <FileText size={32} color="#ccc" />
                          <p>Le compte rendu se génère automatiquement pendant la réunion...</p>
                          <button style={styles.emptyButton} onClick={generateSummary} disabled={isGeneratingSummary}>
                            {isGeneratingSummary ? <Loader2 size={14} className="spin" /> : "Générer maintenant"}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div style={styles.contentActions}>
                  {!editCR ? (
                    <>
                      <button style={styles.actionButton} onClick={startEditCR}>
                        <Edit3 size={14} /> Modifier
                      </button>
                      <button style={styles.actionButton} onClick={() => download(compteRendu, "compte_rendu.txt")}>
                        <Download size={14} /> Télécharger
                      </button>
                    </>
                  ) : (
                    <>
                      <button style={styles.actionButtonPrimary} onClick={saveEditCR}>
                        <CheckCircle size={14} /> OK
                      </button>
                      <button style={styles.actionButton} onClick={cancelEditCR}>
                        Annuler
                      </button>
                      <button style={styles.actionButton} onClick={() => download(tempCompteRendu, "compte_rendu.txt")}>
                        <Download size={14} /> Télécharger
                      </button>
                    </>
                  )}
                </div>
              </>
            )}

            {activeTab === 'tasks' && (
              <>
                <div style={styles.tabHeader}>
                  <span style={styles.tabHeaderTitle}>
                    <CheckSquare size={14} color="#5e17eb" />
                    Liste des tâches
                  </span>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      style={styles.generateButton}
                      onClick={generateTasks}
                      disabled={isExtractingTasks}
                    >
                      {isExtractingTasks ? <Loader2 size={14} className="spin" /> : <RotateCcw size={14} />}
                      <span>{isExtractingTasks ? "Extraction..." : "Extraire"}</span>
                    </button>
                    <button
                      style={styles.addButton}
                      onClick={addTask}
                    >
                      <UserPlus size={14} />
                      <span>Ajouter</span>
                    </button>
                  </div>
                </div>

                {editTasks ? (
                  <div style={styles.tasksEditContainer}>
                    {tempTasks.map((task, idx) => (
                      <div key={task.id} style={styles.taskEditCard}>
                        <textarea
                          style={styles.taskEditDescription}
                          value={task.description}
                          onChange={(e) => {
                            const newTasks = [...tempTasks]
                            newTasks[idx].description = e.target.value
                            setTempTasks(newTasks)
                          }}
                          placeholder="Description de la tâche"
                        />
                        <div style={styles.taskEditMeta}>
                          <select
                            style={styles.taskEditSelect}
                            value={task.responsableId}
                            onChange={(e) => {
                              const newTasks = [...tempTasks]
                              newTasks[idx].responsableId = e.target.value
                              const participant = participants.find(p => p.id === parseInt(e.target.value))
                              newTasks[idx].responsableName = participant ? participant.name : ""
                              newTasks[idx].responsableEmail = participant ? participant.email : ""
                              setTempTasks(newTasks)
                            }}
                          >
                            <option value="">Assigner à...</option>
                            {participants.map(p => (
                              <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                          </select>
                          <select
                            style={styles.taskEditSelect}
                            value={task.priorite}
                            onChange={(e) => {
                              const newTasks = [...tempTasks]
                              newTasks[idx].priorite = e.target.value
                              setTempTasks(newTasks)
                            }}
                          >
                            <option value="basse">Basse priorité</option>
                            <option value="moyenne">Moyenne priorité</option>
                            <option value="haute">Haute priorité</option>
                            <option value="critique">Critique</option>
                          </select>
                          <input
                            type="date"
                            style={styles.taskEditInput}
                            placeholder="Date limite"
                            value={task.deadline || ""}
                            onChange={(e) => {
                              const newTasks = [...tempTasks]
                              newTasks[idx].deadline = e.target.value
                              setTempTasks(newTasks)
                            }}
                          />
                          <button
                            style={styles.taskDeleteButton}
                            onClick={() => {
                              setTempTasks(tempTasks.filter((_, i) => i !== idx))
                            }}
                          >
                            <X size={14} /> Supprimer
                          </button>
                        </div>
                      </div>
                    ))}
                    {tempTasks.length === 0 && (
                      <div style={styles.taskEmpty}>Aucune tâche. Cliquez sur "Ajouter" pour en créer une.</div>
                    )}
                  </div>
                ) : (
                  <div style={styles.tasksList}>
                    {tasks.length > 0 ? (
                      tasks.map((task, index) => {
                        const priorityColor = getPriorityColor(task.priorite)
                        const priorityBg = getPriorityBg(task.priorite)
                        const responsableName = task.responsableName || getParticipantName(task.responsableId)
                        const responsableEmail = task.responsableEmail || getParticipantEmail(task.responsableId) || generateEmailFromName(responsableName)
                        return (
                          <div key={task.id} style={styles.taskCardStyled}>
                            <div style={styles.taskNumber}>{index + 1}</div>
                            <div style={styles.taskContent}>
                              <div style={styles.taskHeaderStyled}>
                                <span style={styles.taskDescriptionStyled}>{task.description}</span>
                              </div>
                              <div style={styles.taskMetaStyled}>
                                {responsableName && (
                                  <div style={styles.taskResponsableStyled}>
                                    <User size={12} />
                                    <span>{responsableName}</span>
                                    {responsableEmail && (
                                      <span style={styles.taskEmailStyled}>
                                        <Mail size={10} /> {responsableEmail}
                                      </span>
                                    )}
                                  </div>
                                )}
                                <span style={{ ...styles.taskPriorityStyled, backgroundColor: priorityBg, color: priorityColor }}>
                                  <Flag size={10} /> {task.priorite}
                                </span>
                                {task.deadline && (
                                  <span style={styles.taskDeadlineStyled}>
                                    <Calendar size={10} /> {new Date(task.deadline).toLocaleDateString('fr-FR')}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        )
                      })
                    ) : (
                      <div style={styles.taskEmpty}>
                        <CheckSquare size={32} color="#ccc" />
                        <p>Aucune tâche pour le moment. Cliquez sur "Extraire" pour générer des tâches intelligemment.</p>
                        <button style={styles.emptyButton} onClick={generateTasks} disabled={isExtractingTasks}>
                          {isExtractingTasks ? <Loader2 size={14} className="spin" /> : "Extraire les tâches"}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <div style={styles.contentActions}>
                  {!editTasks ? (
                    <button style={styles.actionButton} onClick={startEditTasks}>
                      <Edit3 size={14} /> Modifier
                    </button>
                  ) : (
                    <>
                      <button style={styles.actionButtonPrimary} onClick={saveEditTasks}>
                        <CheckCircle size={14} /> OK
                      </button>
                      <button style={styles.actionButton} onClick={cancelEditTasks}>
                        Annuler
                      </button>
                    </>
                  )}
                  <button style={styles.actionButton} onClick={() => download(tasks.map(t => `• ${t.description}\n   Responsable: ${t.responsableName || getParticipantName(t.responsableId) || 'Non assigné'} (${t.responsableEmail || generateEmailFromName(t.responsableName) || 'Email non disponible'})\n   Priorité: ${t.priorite}${t.deadline ? '\n   Échéance: ' + new Date(t.deadline).toLocaleDateString('fr-FR') : ''}`).join('\n\n'), "taches.txt")}>
                    <Download size={14} /> Télécharger
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Instruction Tooltip for Testing */}
      <div style={{
        position: 'fixed',
        bottom: 20,
        left: 20,
        background: '#f0f9ff',
        borderLeft: `4px solid #667eea`,
        padding: '12px 16px',
        borderRadius: 12,
        fontSize: 12,
        color: '#334155',
        maxWidth: 280,
        zIndex: 999,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>📋 Pour tester l'application:</div>
        <div>1️⃣ Cliquez "Démarrer" (microphone)</div>
        <div>2️⃣ Cliquez "Jouer le test audio"</div>
        <div>3️⃣ Observez la transcription en temps réel</div>
        <div>4️⃣ La traduction, le CR et les tâches se génèrent automatiquement</div>
      </div>

      <FinalizeMeetingPopup
        isOpen={showFinalizePopup}
        onClose={() => setShowFinalizePopup(false)}
        meetingId={meetingId}
        meetingTitle={meetingDetails?.titre}
        initialCompteRendu={compteRendu}
        initialTasks={tasks}
        onSave={handleFinalizeSave}
      />

      <QuitMeetingPopup
        isOpen={showQuitPopup}
        onClose={() => setShowQuitPopup(false)}
        onConfirm={handleConfirmQuit}
        meetingTitle={meetingDetails?.titre}
      />

      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes pulse {
          0% { transform: scale(1); opacity: 1; }
          100% { transform: scale(1.2); opacity: 0.5; }
        }
      `}</style>
    </div>
  )
}

const styles = {
  container: {
    background: "#f7f6ff",
    minHeight: "100vh",
    padding: 20,
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  notification: {
    position: "fixed",
    top: 20,
    right: 20,
    zIndex: 10000,
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 20px",
    borderRadius: 12,
    color: "white",
    fontSize: 14,
    fontWeight: 500,
    boxShadow: "0 4px 15px rgba(0,0,0,0.15)",
  },
  header: {
    background: "white",
    padding: "20px 24px",
    borderRadius: 20,
    marginBottom: 20,
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
    transition: "all 0.3s ease",
  },
  headerTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 15,
  },
  titleSection: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  title: {
    color: "#5e17eb",
    fontSize: 22,
    margin: 0,
    fontWeight: 600,
  },
  recordingBadge: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "#fee2e2",
    padding: "4px 12px",
    borderRadius: 20,
    color: "#dc2626",
    fontSize: 12,
    fontWeight: 500,
  },
  recordingDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: "#dc2626",
    animation: "pulse 1s infinite",
  },
  headerButtons: {
    display: "flex",
    gap: 8,
  },
  headerBtnGreen: {
    padding: "8px 20px",
    borderRadius: 40,
    background: "#22c55e",
    color: "white",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 14,
    fontWeight: 500,
    transition: "all 0.2s ease",
    ":hover": {
      transform: "scale(1.02)",
      background: "#16a34a",
    },
  },
  headerBtnRed: {
    padding: "8px 20px",
    borderRadius: 40,
    background: "#f44336",
    color: "white",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 14,
    fontWeight: 500,
    transition: "all 0.2s ease",
    ":hover": {
      transform: "scale(1.02)",
      background: "#dc2626",
    },
  },
  headerBtnDark: {
    padding: "8px 20px",
    borderRadius: 40,
    background: "#333",
    color: "white",
    border: "none",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 500,
    transition: "all 0.2s ease",
    ":hover": {
      transform: "scale(1.02)",
      background: "#1f2937",
    },
  },
  headerBtnQuit: {
    padding: "8px 20px",
    borderRadius: 40,
    background: "#f0f0f0",
    color: "#666",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 14,
    fontWeight: 500,
    transition: "all 0.2s ease",
    ":hover": {
      background: "#e5e5e5",
    },
  },
  meetingInfo: {
    display: "flex",
    gap: 24,
    flexWrap: "wrap",
    padding: "12px 0",
    borderTop: "1px solid #f0f0f0",
    alignItems: "center",
  },
  infoItem: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 14,
    color: "#444",
  },
  participantsHeaderCompact: {
    position: "relative",
    marginLeft: "auto",
  },
  participantsButton: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 12px",
    background: "#ede9ff",
    border: "none",
    borderRadius: 30,
    cursor: "pointer",
    color: "#5e17eb",
    fontSize: 13,
    fontWeight: 500,
    transition: "all 0.2s ease",
    ":hover": {
      background: "#e0d9ff",
    },
  },
  participantsCountCompact: {
    background: "#5e17eb",
    color: "white",
    padding: "2px 8px",
    borderRadius: 20,
    fontSize: 11,
    marginLeft: 4,
  },
  participantsDropdown: {
    position: "absolute",
    top: "100%",
    right: 0,
    marginTop: 8,
    background: "white",
    borderRadius: 16,
    boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
    zIndex: 1000,
    minWidth: 320,
    border: "1px solid #ede9ff",
    animation: "slideIn 0.2s ease-out",
  },
  participantsDropdownHeader: {
    padding: "16px",
    borderBottom: "1px solid #f0f0f0",
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontWeight: 600,
    color: "#333",
  },
  participantsDropdownList: {
    maxHeight: 300,
    overflowY: "auto",
    padding: "8px",
  },
  participantDropdownItem: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 12px",
    borderRadius: 12,
    transition: "all 0.2s ease",
    ":hover": {
      background: "#f8f7ff",
    },
  },
  participantDropdownAvatar: {
    width: 32,
    height: 32,
    borderRadius: 32,
    background: "#5e17eb",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 600,
  },
  participantDropdownInfo: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  participantDropdownName: {
    fontSize: 13,
    fontWeight: 500,
    color: "#333",
  },
  participantDropdownEmail: {
    fontSize: 11,
    color: "#666",
  },
  participantDropdownStatus: {
    display: "flex",
    alignItems: "center",
    minWidth: 90,
  },
  mainArea: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 20,
    height: "calc(100vh - 200px)",
  },
  leftPanel: {
    background: "white",
    borderRadius: 24,
    padding: 24,
    display: "flex",
    flexDirection: "column",
    gap: 20,
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
    transition: "all 0.3s ease",
  },
  panelHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  panelTitle: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    fontWeight: 600,
    color: "#333",
    letterSpacing: "1px",
  },
  langBadge: {
    padding: "4px 10px",
    borderRadius: 20,
    backgroundColor: '#f0f0f0',
    color: '#666',
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 12,
  },
  transcriptionArea: {
    flex: 1,
    background: "#f9f9ff",
    borderRadius: 16,
    padding: 20,
    overflowY: "auto",
    border: "1px solid #e5e7eb",
  },
  transcriptText: {
    whiteSpace: "pre-wrap",
    fontSize: 15,
    lineHeight: 1.6,
    color: "#333",
  },
  interimText: {
    color: "#999",
    fontStyle: "italic",
    marginTop: 10,
    fontSize: 15,
    lineHeight: 1.6,
  },
  placeholderText: {
    color: "#999",
    textAlign: "center",
    marginTop: 40,
    fontSize: 14,
  },
  rightPanel: {
    background: "white",
    borderRadius: 24,
    padding: 24,
    display: "flex",
    flexDirection: "column",
    gap: 20,
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
    transition: "all 0.3s ease",
  },
  tabBar: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 8,
    background: "#f5f5f5",
    padding: 4,
    borderRadius: 100,
  },
  tab: {
    padding: "10px 16px",
    borderRadius: 100,
    border: "none",
    background: "transparent",
    color: "#666",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.2s ease",
    ":hover": {
      color: "#5e17eb",
    },
  },
  tabActive: {
    padding: "10px 16px",
    borderRadius: 100,
    border: "none",
    background: "white",
    color: "#5e17eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  },
  tabLang: {
    fontSize: 16,
  },
  badgeNew: {
    background: "#ef4444",
    color: "white",
    fontSize: 10,
    padding: "2px 6px",
    borderRadius: 10,
    marginLeft: 6,
  },
  badgeCount: {
    background: "#5e17eb",
    color: "white",
    fontSize: 10,
    padding: "2px 6px",
    borderRadius: 10,
    marginLeft: 6,
  },
  tabContent: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  tabHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  translationControls: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  translationLabel: {
    fontSize: 13,
    color: "#666",
  },
  langSelector: {
    position: "relative",
  },
  langSelectorButton: {
    padding: "8px 16px",
    background: "#f0f0f0",
    border: "none",
    borderRadius: 40,
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    cursor: "pointer",
    transition: "all 0.2s ease",
    ":hover": {
      background: "#e5e5e5",
    },
  },
  langDropdown: {
    position: "absolute",
    top: "100%",
    left: 0,
    background: "white",
    borderRadius: 16,
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    marginTop: 5,
    zIndex: 10,
    minWidth: 180,
    border: "1px solid #eee",
    animation: "slideIn 0.2s ease-out",
  },
  langOption: {
    padding: "10px 16px",
    display: "flex",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
    fontSize: 13,
    transition: "all 0.2s ease",
    ":hover": {
      background: "#f5f5f5",
    },
  },
  tabHeaderTitle: {
    fontSize: 14,
    fontWeight: 500,
    color: "#333",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  generateButton: {
    padding: "6px 12px",
    borderRadius: 40,
    background: "#ede9ff",
    color: "#5e17eb",
    border: "none",
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    cursor: "pointer",
    transition: "all 0.2s ease",
    ":hover": {
      background: "#e0d9ff",
      transform: "scale(1.02)",
    },
    ":disabled": {
      opacity: 0.6,
      cursor: "not-allowed",
    },
  },
  addButton: {
    padding: "6px 12px",
    borderRadius: 40,
    background: "#22c55e",
    color: "white",
    border: "none",
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    cursor: "pointer",
    transition: "all 0.2s ease",
    ":hover": {
      background: "#16a34a",
      transform: "scale(1.02)",
    },
  },
  contentTextarea: {
    flex: 1,
    padding: 16,
    border: "1px solid #e5e7eb",
    borderRadius: 16,
    fontSize: 14,
    fontFamily: "monospace",
    lineHeight: 1.6,
    resize: "none",
    background: "#f9f9ff",
    color: "#333",
  },
  contentActions: {
    display: "flex",
    gap: 8,
    justifyContent: "flex-end",
  },
  actionButton: {
    padding: "6px 16px",
    borderRadius: 40,
    border: "1px solid #ddd",
    background: "white",
    color: "#666",
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    cursor: "pointer",
    transition: "all 0.2s ease",
    ":hover": {
      background: "#f5f5f5",
      borderColor: "#ccc",
    },
  },
  actionButtonPrimary: {
    padding: "6px 16px",
    borderRadius: 40,
    border: "none",
    background: "#22c55e",
    color: "white",
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    cursor: "pointer",
    transition: "all 0.2s ease",
    ":hover": {
      background: "#16a34a",
      transform: "scale(1.02)",
    },
  },
  crCard: {
    background: 'white',
    borderRadius: 16,
    overflow: 'hidden',
    border: '1px solid #e5e7eb',
    marginBottom: 16,
    flex: 1,
    transition: "all 0.3s ease",
  },
  crHeader: {
    background: 'linear-gradient(135deg, #5e17eb 0%, #7c3aed 100%)',
    padding: '12px 16px',
    color: 'white',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  crTitle: {
    fontSize: 14,
    fontWeight: 600,
    margin: 0,
  },
  crDate: {
    fontSize: 11,
    opacity: 0.8,
  },
  crEditTextarea: {
    width: '100%',
    padding: 16,
    border: 'none',
    fontSize: 14,
    lineHeight: 1.6,
    resize: 'vertical',
    fontFamily: 'inherit',
    minHeight: 300,
    background: '#fff',
    outline: 'none',
  },
  crContent: {
    padding: 16,
    background: '#f9f9ff',
    minHeight: 300,
    maxHeight: 400,
    overflowY: 'auto',
  },
  crText: {
    fontSize: 14,
    lineHeight: 1.6,
    color: '#333',
  },
  crParagraph: {
    margin: '0 0 10px 0',
  },
  crEmpty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    textAlign: 'center',
    color: '#999',
  },
  emptyButton: {
    marginTop: 16,
    padding: "8px 20px",
    borderRadius: 40,
    background: "#5e17eb",
    color: "white",
    border: "none",
    cursor: "pointer",
    fontSize: 13,
    display: "flex",
    alignItems: "center",
    gap: 8,
    transition: "all 0.2s ease",
    ":hover": {
      background: "#4c12c4",
      transform: "scale(1.02)",
    },
    ":disabled": {
      opacity: 0.6,
      cursor: "not-allowed",
    },
  },
  tasksList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    flex: 1,
    maxHeight: 400,
    overflowY: 'auto',
  },
  tasksEditContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    flex: 1,
    maxHeight: 400,
    overflowY: 'auto',
  },
  taskEditCard: {
    background: '#f8f7ff',
    borderRadius: 12,
    padding: 14,
    border: '1px solid #ede9ff',
    transition: "all 0.2s ease",
  },
  taskEditDescription: {
    width: '100%',
    padding: 8,
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    fontSize: 14,
    fontFamily: 'inherit',
    resize: 'vertical',
    minHeight: 60,
    marginBottom: 10,
  },
  taskEditMeta: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  taskEditSelect: {
    padding: '6px 10px',
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    fontSize: 12,
    background: 'white',
    cursor: 'pointer',
  },
  taskEditInput: {
    padding: "6px 10px",
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    fontSize: 12,
    background: "white",
    flex: 1,
    minWidth: "120px",
  },
  taskDeleteButton: {
    padding: '4px 8px',
    borderRadius: 8,
    border: '1px solid #f44336',
    background: 'white',
    color: '#f44336',
    cursor: 'pointer',
    display: 'flex',
    alignItems: "center",
    gap: 4,
    fontSize: 12,
    transition: "all 0.2s ease",
    ":hover": {
      background: "#f44336",
      color: "white",
    },
  },
  taskCardStyled: {
    background: "#f8f7ff",
    borderRadius: 12,
    padding: 14,
    border: "1px solid #ede9ff",
    display: "flex",
    gap: 12,
    transition: "all 0.2s ease",
    ":hover": {
      transform: "translateX(4px)",
      boxShadow: "0 4px 12px rgba(94,23,235,0.1)",
    },
  },
  taskNumber: {
    width: 28,
    height: 28,
    borderRadius: 28,
    background: "#ede9ff",
    color: "#5e17eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 600,
    flexShrink: 0,
  },
  taskContent: {
    flex: 1,
  },
  taskHeaderStyled: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 8,
  },
  taskDescriptionStyled: {
    fontSize: 14,
    fontWeight: 500,
    color: "#1f1f2e",
    flex: 1,
  },
  taskMetaStyled: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginTop: 8,
    fontSize: 12,
    flexWrap: "wrap",
  },
  taskResponsableStyled: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    color: "#5e17eb",
  },
  taskEmailStyled: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    color: "#666",
    fontSize: 10,
    marginLeft: 4,
  },
  taskPriorityStyled: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: "2px 8px",
    borderRadius: 12,
    fontSize: 10,
    fontWeight: 500,
  },
  taskDeadlineStyled: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    color: "#666",
    fontSize: 10,
  },
  taskEmpty: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: 40,
    textAlign: "center",
    color: "#999",
    background: "#f9f9ff",
    borderRadius: 12,
  },
  errorToast: {
    position: "fixed",
    bottom: 20,
    right: 20,
    background: "#f44336",
    color: "white",
    padding: "12px 24px",
    borderRadius: 100,
    fontSize: 14,
    boxShadow: "0 4px 12px rgba(244,67,54,0.3)",
    zIndex: 1000,
    animation: "slideIn 0.3s ease-out",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100vh",
    background: "#f7f6ff",
  },
  loadingSpinner: {
    width: 50,
    height: 50,
    border: "3px solid #e0e0e0",
    borderTop: "3px solid #5e17eb",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    marginBottom: 20,
  },
  loadingText: {
    color: "#666",
    fontSize: 16,
    textAlign: "center",
  },
}

const styleSheet = document.createElement("style")
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  @keyframes pulse {
    0% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.2); opacity: 0.5; }
    100% { transform: scale(1); opacity: 1; }
  }
`
document.head.appendChild(styleSheet)